import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import type { PortalConfirmField, PortalConfirmOptions } from '../../../services/portal-dialog.types';

@Component({
  selector: 'app-portal-confirm-dialog',
  templateUrl: './portal-confirm-dialog.component.html',
  styleUrls: ['./portal-confirm-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortalConfirmDialogComponent implements OnInit {
  @Input() options: PortalConfirmOptions = { title: '' };

  busy = false;
  title = '';
  message = '';
  confirmLabel = '';
  cancelLabel = '';
  confirmVariant: 'primary' | 'danger' = 'primary';
  size: 'sm' | 'md' | 'lg' = 'sm';
  fields: PortalConfirmField[] = [];
  drafts: Record<string, string> = {};
  fieldError = '';
  fieldErrorKey = '';

  constructor(
    public activeModal: NgbActiveModal,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const o = this.options || { title: '' };
    this.title = o.title || '';
    this.message = o.message || '';
    this.confirmLabel = o.confirmLabel || '';
    this.cancelLabel = o.cancelLabel || '';
    this.confirmVariant = o.confirmVariant === 'danger' ? 'danger' : 'primary';
    this.size = o.size || 'sm';
    this.fields = Array.isArray(o.fields) ? o.fields.filter((field) => !!field?.label) : [];
    this.drafts = {};
    for (const field of this.fields) {
      if (field.key && field.editable) {
        this.drafts[field.key] = field.value || '';
      }
    }
  }

  fieldValue(field: PortalConfirmField): string {
    return field.key && field.editable ? (this.drafts[field.key] ?? '') : (field.value || '');
  }

  onFieldChange(field: PortalConfirmField, value: string): void {
    if (!field.key) {
      return;
    }
    this.drafts[field.key] = value;
    this.fieldError = '';
    this.fieldErrorKey = '';
    this.cdr.markForCheck();
  }

  onCheckboxChange(field: PortalConfirmField, checked: boolean): void {
    this.onFieldChange(field, checked ? '1' : '0');
  }

  isCheckbox(field: PortalConfirmField): boolean {
    return field.type === 'checkbox';
  }

  private collectedValues(): Record<string, string> {
    const values: Record<string, string> = {};
    for (const field of this.fields) {
      if (field.key && field.editable) {
        values[field.key] = (this.drafts[field.key] ?? '').trim();
      }
    }
    return values;
  }

  private missingRequired(): PortalConfirmField | null {
    return this.fields.find((field) => {
      if (!field.editable || !field.required || !field.key) {
        return false;
      }
      return !(this.drafts[field.key] ?? '').trim();
    }) || null;
  }

  private isEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  dismiss(): void {
    if (this.busy) return;
    this.activeModal.dismiss('cancel');
  }

  async confirm(): Promise<void> {
    if (this.busy) return;
    const missing = this.missingRequired();
    if (missing) {
      this.fieldError = missing.label;
      this.fieldErrorKey = '';
      this.cdr.markForCheck();
      return;
    }
    const invalidEmail = this.fields.find((field) => {
      if (field.type !== 'email' || !field.editable || !field.key) {
        return false;
      }
      return !this.isEmail(this.drafts[field.key] ?? '');
    });
    if (invalidEmail) {
      this.fieldError = '';
      this.fieldErrorKey = 'VALIDATION.EMAIL_INVALID';
      this.cdr.markForCheck();
      return;
    }
    const values = this.collectedValues();
    const onConfirm = this.options?.onConfirm;
    if (!onConfirm) {
      this.activeModal.close(Object.keys(values).length ? values : true);
      return;
    }
    this.busy = true;
    this.cdr.markForCheck();
    try {
      await onConfirm(values);
      this.activeModal.close(Object.keys(values).length ? values : true);
    } catch {
      // Keep open; caller should toast
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }
}
